import Image from 'next/image'
import React from 'react'
import { HiOutlineStar } from 'react-icons/hi'
import { BuildingApi } from 'src/common/types'
import { segment } from 'src/lib/Segments'

import { ShortRating } from '@components/index'
import { AiOutlineCalendar } from '@react-icons/all-files/ai/AiOutlineCalendar'
import { FaRegBuilding } from '@react-icons/all-files/fa/FaRegBuilding'
import { GiStairs } from '@react-icons/all-files/gi/GiStairs'
import { titleCase } from '@utility/Utilities'

interface Props {
  building: BuildingApi
  customCardStyle?: string
  showImage?: boolean
  customImageStyle?: string
  customTextStyle?: string
  showSaveIcon?: boolean
  showBuildingDetails?: boolean
  showKeywords?: boolean
  showTagsAsLinks?: boolean
  segmentBase?: string
  city?: string
}

export default function NewBuildingCard({
  building,
  showImage = true,
  customImageStyle = 'h-40',
  showBuildingDetails = false,
  customCardStyle,
  segmentBase = 'other',
}: Props): JSX.Element {
  const imageUrl = building?.images?.[0]?.url ?? '/listing-placeholder.jpg'

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border border-light-40 bg-white ${customCardStyle}`}
    >
      {showImage && (
        <div className={`relative h-40 w-full ${customImageStyle} flex-1 overflow-hidden`}>
          <a
            onClick={() =>
              segment?.[segmentBase + '_buildingcard']?.(
                building.slug,
                building?.primaryAddress?.slug
              )
            }
            href={`/building/${building.primaryAddress.cityId}/${building.slug}/${building?.primaryAddress?.slug}`}
            className="relative flex h-full flex-col justify-between"
            title={building?.primaryAddress?.fullAddress}
          >
            <Image
              src={imageUrl}
              alt="building"
              layout="fill"
              objectPosition="center"
              objectFit="cover"
              loading="lazy"
              unoptimized
            />
          </a>
        </div>
      )}

      {building.neighborhood && (
        <div className="mt-4 flex px-4 text-base font-semibold text-mid-400">
          {building.neighborhood.name}
        </div>
      )}

      <div
        className={`flex flex-col ${showBuildingDetails ? 'h-56' : 'h-48'}`}
        onClick={() =>
          segment.global_buildingcard?.(segmentBase, building.slug, building?.primaryAddress.slug)
        }
      >
        <div className="px-4 pt-2 pr-10">
          <a
            href={`/building/${building?.primaryAddress?.cityId}/${building.slug}/${building?.primaryAddress?.slug}`}
            title={building?.primaryAddress?.fullAddress}
          >
            <div className="lineclamp-2 !font-semibold text-dark-900">
              {building?.name ? <>{titleCase(building.name)} at</> : null}{' '}
              {titleCase(building?.primaryAddress?.fullAddress)}
            </div>
          </a>
          {building.averageRating !== null && (
            <div className="flex items-center py-1">
              <ShortRating
                value={building.averageRating}
                count={building.reviewCount}
                titleStyle="text-sm font-bold"
                countStyle="text-sm"
                containerStyle=" justify-self-end"
              />
              {building?.averageRating >= 4.3 && building?.reviewCount >= 10 && (
                <div className="ml-4 flex items-center rounded-md border border-yellow-400 py-1 px-2">
                  <HiOutlineStar size={18} color="#FF991F" />
                  <span className="pl-2 text-xs font-semibold text-dark-900">Top Rated</span>
                </div>
              )}
            </div>
          )}
        </div>
        {showBuildingDetails && (
          <div className="flex w-full flex-row flex-wrap px-4">
            {building.numUnits && (
              <div className="inline-flex items-center space-x-1 pr-2">
                <FaRegBuilding size={12} color="#050045" />
                <span className="text-xs text-gray-600">{building.numUnits} Units</span>
              </div>
            )}
            {building.numFloors && (
              <div className="inline-flex items-center space-x-1 pr-2">
                <GiStairs size={12} color="#050045" />
                <span className="text-xs text-gray-600">{building.numFloors} Floors</span>
              </div>
            )}
            {building.builtYear && (
              <div className="inline-flex items-center space-x-1">
                <AiOutlineCalendar size={12} color="#050045" />
                <span className="text-xs text-gray-600">Built {building.builtYear}</span>
              </div>
            )}
          </div>
        )}
        <div
          className={`mx-4 mt-2 rounded-md px-4 py-2 text-sm text-dark-900 ${
            building?.openDataSummary?.numOpenViolations ? 'bg-yellow-75' : 'bg-light-10'
          }`}
        >
          Open Violations: {building?.openDataSummary?.numOpenViolations ?? 0}
        </div>
        <div
          className={`mx-4 mt-2 rounded-md px-4 py-2 text-sm text-dark-900 ${
            building?.openDataSummary?.numBedbugInfestations1Y ? 'bg-yellow-75' : 'bg-light-10'
          }`}
        >
          1 Year Bedbug History:{' '}
          {building?.openDataSummary?.numBedbugInfestations1Y > 0 ? 'Yes' : 'No'}
        </div>
      </div>
    </div>
  )
}
